import { defineStore } from 'pinia'
import { v4 as uuid } from 'uuid'
import { useI18n } from 'vue-i18n'
import { PageResponse, PageData, Block } from '~/models/page-editor'
import PageEditorTitleBlock from '~/components/page-editor/title'
import PageEditorImagesBlock from '~/components/page-editor/images'
import PageEditorMarkdownBlock from '~/components/page-editor/markdown'
import PageEditorContentBlock from '~/components/page-editor/content'
import PageEditorTicketsBlock from '~/components/page-editor/tickets'
import PageEditorSpeakersBlock from '~/components/page-editor/speakers'
import PageEditorCardsBlock from '~/components/page-editor/cards'

interface StateInterface {
  page: PageData | null
  isProcessing: boolean
  isEditMode: boolean
  isLoaded: boolean
}

export const usePageEditor = defineStore('pageEditor', () => {
  const baseURL = useRuntimeConfig().public.gatewayApi
  const adminURL = useRuntimeConfig().public.gatewayAdminApi
  const { locale } = useI18n()

  const state = reactive<StateInterface>({
    page: null,
    isProcessing: false,
    isEditMode: false,
    isLoaded: false,
  })

  // keep in mind that each type of block must have its own id (in index.ts)
  const blocksList = [
    {
      ...PageEditorTitleBlock,
    },
    {
      ...PageEditorImagesBlock,
    },
    {
      ...PageEditorMarkdownBlock,
    },
    {
      ...PageEditorContentBlock,
    },
    {
      ...PageEditorTicketsBlock,
    },
    {
      ...PageEditorSpeakersBlock,
    },
    {
      ...PageEditorCardsBlock,
    },
  ]

  const blocksMap = blocksList.reduce((map, item) => {
    map[item.id] = item
    return map
  }, {} as { [key: number]: any })

  const blocksToString = (blocks: any) => {
    return JSON.stringify(
      blocks.map((block: any) => {
        return { id: block.id, uuid: block.uuid, editorData: block.editorData }
      })
    )
  }

  const editorBlocks: ComputedRef<Block[]> = computed(() => {
    if (state.page?.data) {
      const data = JSON.parse(state.page.data)

      return data.map((item: any) => ({
        ...blocksMap[item.id],
        ...{ editorData: item.editorData },
        uuid: item.uuid ?? uuid(),
        key: Symbol('key'),
      }))
    } else {
      return []
    }
  })

  const fetchPage = async (rawUrl: string, isAdmin: boolean) => {
    let url = rawUrl

    // we want to see same page constructor data for all languages
    if (rawUrl.includes(`/${locale.value}/`)) {
      url = rawUrl.replace(`/${locale.value}`, '')
    }
    if (state.page?.url?.toLowerCase() === url.toLowerCase()) {
      return
    }

    state.isProcessing = true
    state.page = null

    try {
      const data = await useAuthFetch<PageResponse>('/page', {
        baseURL: isAdmin ? adminURL : baseURL,
        params: {
          url,
        },
      })

      state.page = data

      // we need to upgrade config with block uuids
      const isNeedToSave = JSON.parse(state.page?.data as string).some(
        (item: any) => !item.uuid
      )

      if (isNeedToSave) {
        await updatePage(
          state.page.id as number,
          state.page.url,
          editorBlocks.value
        )
      }
    } catch (error: any) {
      if (error.status !== 404) {
        useLogError(error)
      }
      state.page = { url }
    } finally {
      state.isLoaded = true
      state.isProcessing = false
    }
  }

  const createPage = async (url: string, blocks: any) => {
    state.isProcessing = true

    try {
      const data = await useAuthFetch<PageResponse>('/page', {
        baseURL: adminURL,
        method: 'POST',
        body: {
          url,
          data: blocksToString(blocks),
        },
      })

      state.page = data
    } catch (error) {
      useLogError(error, true)
    } finally {
      state.isProcessing = false
    }
  }

  const updatePage = async (id: number, url: string, blocks: any) => {
    state.isProcessing = true

    try {
      const data = await useAuthFetch<PageResponse>(`/page/${id}`, {
        baseURL: adminURL,
        method: 'PUT',
        body: {
          url,
          data: blocksToString(blocks),
        },
      })

      state.page = data
    } catch (error) {
      useLogError(error, true)
    } finally {
      state.isProcessing = false
    }
  }

  const deletePage = async (id: number) => {
    state.isProcessing = true

    try {
      await useAuthFetch(`/page/${id}`, {
        baseURL: adminURL,
        method: 'DELETE',
      })

      state.page = null
    } catch (error) {
      useLogError(error, true)
    } finally {
      state.isProcessing = false
    }
  }

  const toggleEditMode = () => {
    state.isEditMode = !state.isEditMode
  }

  const turnOffEditMode = () => {
    state.isEditMode = false
  }

  const resetPage = () => {
    state.isLoaded = false
    state.page = null
  }

  return {
    page: toRef(state, 'page'),
    isEditMode: toRef(state, 'isEditMode'),
    isProcessing: toRef(state, 'isProcessing'),
    isLoaded: toRef(state, 'isLoaded'),
    editorBlocks,
    blocksList,
    blocksMap,
    fetchPage,
    createPage,
    updatePage,
    deletePage,
    toggleEditMode,
    resetPage,
    turnOffEditMode,
  }
})
